import PropTypes from 'prop-types';
import { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import { cn } from 'Utils/cn';

function SortableColumn({ column, onToggle, onMoveUp, onMoveDown, isFirst, isLast, isNested = false }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: column.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-gray-100 text-sm',
                isDragging && 'shadow-md z-10 opacity-90',
                isNested && 'ml-6 bg-gray-50',
            )}
        >
            <div
                {...attributes}
                {...listeners}
                className="cursor-grab text-gray-400 hover:text-gray-600 shrink-0 touch-none"
            >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="5" cy="3" r="1.5" />
                    <circle cx="11" cy="3" r="1.5" />
                    <circle cx="5" cy="8" r="1.5" />
                    <circle cx="11" cy="8" r="1.5" />
                    <circle cx="5" cy="13" r="1.5" />
                    <circle cx="11" cy="13" r="1.5" />
                </svg>
            </div>

            <Checkbox checked={column.active} onChange={() => onToggle(column.id)} className="grow min-w-0">
                <span className="truncate text-sm">{column.label}</span>
            </Checkbox>

            <div className="flex flex-col shrink-0">
                <button
                    type="button"
                    onClick={() => onMoveUp(column.id)}
                    disabled={isFirst}
                    className={cn(
                        'p-0.5 rounded transition-colors',
                        isFirst
                            ? 'text-gray-200 cursor-not-allowed'
                            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                    )}
                    aria-label="Mover arriba"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15" />
                    </svg>
                </button>
                <button
                    type="button"
                    onClick={() => onMoveDown(column.id)}
                    disabled={isLast}
                    className={cn(
                        'p-0.5 rounded transition-colors',
                        isLast
                            ? 'text-gray-200 cursor-not-allowed'
                            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                    )}
                    aria-label="Mover abajo"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

SortableColumn.propTypes = {
    column: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        active: PropTypes.bool.isRequired,
    }).isRequired,
    onToggle: PropTypes.func.isRequired,
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    isFirst: PropTypes.bool.isRequired,
    isLast: PropTypes.bool.isRequired,
    isNested: PropTypes.bool,
};

function NestedColumn({ column, onToggle, onMoveUp, onMoveDown, isFirst, isLast }) {
    return (
        <div className="flex items-center gap-2 pl-7 pr-2 py-1.5 rounded bg-gray-50 border border-gray-100 text-sm">
            <Checkbox checked={column.active} onChange={() => onToggle(column.id)} className="grow min-w-0">
                <span className="truncate text-sm">{column.label}</span>
            </Checkbox>

            <div className="flex flex-col shrink-0">
                <button
                    type="button"
                    onClick={() => onMoveUp(column.id)}
                    disabled={isFirst}
                    className={cn(
                        'p-0.5 rounded transition-colors',
                        isFirst
                            ? 'text-gray-200 cursor-not-allowed'
                            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                    )}
                    aria-label="Mover arriba"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15" />
                    </svg>
                </button>
                <button
                    type="button"
                    onClick={() => onMoveDown(column.id)}
                    disabled={isLast}
                    className={cn(
                        'p-0.5 rounded transition-colors',
                        isLast
                            ? 'text-gray-200 cursor-not-allowed'
                            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                    )}
                    aria-label="Mover abajo"
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

NestedColumn.propTypes = {
    column: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        active: PropTypes.bool.isRequired,
    }).isRequired,
    onToggle: PropTypes.func.isRequired,
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    isFirst: PropTypes.bool.isRequired,
    isLast: PropTypes.bool.isRequired,
};

function SortableColumnGroup({
    group,
    groupColumns,
    onToggle,
    onToggleGroup,
    onColumnsChange,
    columns,
    expandedGroups,
    onToggleExpand,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: `group:${group.id}`,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const isExpanded = expandedGroups.includes(group.id);
    const activeCount = groupColumns.filter((c) => c.active).length;
    const allActive = activeCount === groupColumns.length;
    const someActive = activeCount > 0 && !allActive;

    const handleGroupToggle = () => {
        const newActive = !allActive;
        const columnIds = groupColumns.map((col) => col.id);
        onToggleGroup(columnIds, newActive);
    };

    const handleNestedMoveUp = (id) => {
        const index = columns.findIndex((item) => item.id === id);
        if (index > 0) {
            onColumnsChange(arrayMove(columns, index, index - 1));
        }
    };

    const handleNestedMoveDown = (id) => {
        const index = columns.findIndex((item) => item.id === id);
        if (index < columns.length - 1) {
            onColumnsChange(arrayMove(columns, index, index + 1));
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn('border border-gray-200 rounded-md overflow-hidden', isDragging && 'shadow-lg z-10')}
        >
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-100 transition-colors">
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab text-gray-400 hover:text-gray-600 shrink-0 touch-none"
                >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                        <circle cx="5" cy="3" r="1.5" />
                        <circle cx="11" cy="3" r="1.5" />
                        <circle cx="5" cy="8" r="1.5" />
                        <circle cx="11" cy="8" r="1.5" />
                        <circle cx="5" cy="13" r="1.5" />
                        <circle cx="11" cy="13" r="1.5" />
                    </svg>
                </div>
                <Checkbox
                    checked={allActive}
                    indeterminate={someActive}
                    onChange={(e) => {
                        e.stopPropagation();
                        handleGroupToggle();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="grow min-w-0"
                >
                    <span className="font-medium text-sm">{group.label}</span>
                </Checkbox>
                <button
                    type="button"
                    onClick={() => onToggleExpand(group.id)}
                    className="shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
                >
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={cn('transition-transform', isExpanded && 'rotate-90')}
                    >
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
                <span className="text-xs text-gray-500">
                    {activeCount}/{groupColumns.length}
                </span>
                <div className="flex flex-col shrink-0">
                    <button
                        type="button"
                        onClick={() => onMoveUp(`group:${group.id}`)}
                        disabled={isFirst}
                        className={cn(
                            'p-0.5 rounded transition-colors',
                            isFirst
                                ? 'text-gray-200 cursor-not-allowed'
                                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                        )}
                        aria-label="Mover arriba"
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <polyline points="18 15 12 9 6 15" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={() => onMoveDown(`group:${group.id}`)}
                        disabled={isLast}
                        className={cn(
                            'p-0.5 rounded transition-colors',
                            isLast
                                ? 'text-gray-200 cursor-not-allowed'
                                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                        )}
                        aria-label="Mover abajo"
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="p-2 flex flex-col gap-1 bg-white">
                    {groupColumns.map((column, index) => (
                        <NestedColumn
                            key={column.id}
                            column={column}
                            onToggle={onToggle}
                            onMoveUp={handleNestedMoveUp}
                            onMoveDown={handleNestedMoveDown}
                            isFirst={index === 0}
                            isLast={index === groupColumns.length - 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

SortableColumnGroup.propTypes = {
    group: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    }).isRequired,
    groupColumns: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    onToggle: PropTypes.func.isRequired,
    onToggleGroup: PropTypes.func.isRequired,
    onColumnsChange: PropTypes.func.isRequired,
    expandedGroups: PropTypes.array.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    isFirst: PropTypes.bool.isRequired,
    isLast: PropTypes.bool.isRequired,
};

export default function ColumnsSelector({ className, columns, onColumnsChange, groups = [] }) {
    const [expandedGroups, setExpandedGroups] = useState([]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 4,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Crear lista de items ordenables (columnas sin grupo + grupos)
    // Mantener el orden basado en la primera aparición de cada grupo en columns
    const buildOrderedItems = () => {
        const items = [];
        const addedGroups = new Set();

        for (const col of columns) {
            if (col.group) {
                if (!addedGroups.has(col.group)) {
                    addedGroups.add(col.group);
                    items.push({ type: 'group', id: `group:${col.group}`, groupId: col.group });
                }
            } else {
                items.push({ type: 'column', id: col.id, column: col });
            }
        }
        return items;
    };

    const orderedItems = buildOrderedItems();

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const oldIndex = orderedItems.findIndex((item) => item.id === active.id);
        const newIndex = orderedItems.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        // Reordenar los items
        const newOrderedItems = arrayMove(orderedItems, oldIndex, newIndex);

        // Reconstruir el array de columnas basado en el nuevo orden
        const newColumns = [];
        for (const item of newOrderedItems) {
            if (item.type === 'column') {
                newColumns.push(columns.find((c) => c.id === item.id));
            } else {
                // Añadir todas las columnas del grupo en su orden actual
                const groupCols = columns.filter((c) => c.group === item.groupId);
                newColumns.push(...groupCols);
            }
        }
        onColumnsChange(newColumns);
    };

    const handleToggle = (id) => {
        onColumnsChange(columns.map((item) => (item.id === id ? { ...item, active: !item.active } : item)));
    };

    const handleToggleGroup = (columnIds, newActive) => {
        onColumnsChange(columns.map((item) => (columnIds.includes(item.id) ? { ...item, active: newActive } : item)));
    };

    const handleMoveUp = (itemId) => {
        const index = orderedItems.findIndex((item) => item.id === itemId);
        if (index > 0) {
            const newOrderedItems = arrayMove(orderedItems, index, index - 1);
            const newColumns = [];
            for (const item of newOrderedItems) {
                if (item.type === 'column') {
                    newColumns.push(columns.find((c) => c.id === item.id));
                } else {
                    const groupCols = columns.filter((c) => c.group === item.groupId);
                    newColumns.push(...groupCols);
                }
            }
            onColumnsChange(newColumns);
        }
    };

    const handleMoveDown = (itemId) => {
        const index = orderedItems.findIndex((item) => item.id === itemId);
        if (index < orderedItems.length - 1) {
            const newOrderedItems = arrayMove(orderedItems, index, index + 1);
            const newColumns = [];
            for (const item of newOrderedItems) {
                if (item.type === 'column') {
                    newColumns.push(columns.find((c) => c.id === item.id));
                } else {
                    const groupCols = columns.filter((c) => c.group === item.groupId);
                    newColumns.push(...groupCols);
                }
            }
            onColumnsChange(newColumns);
        }
    };

    const handleToggleExpand = (groupId) => {
        setExpandedGroups((prev) =>
            prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
        );
    };

    return (
        <div className={cn('w-full overflow-hidden', className)}>
            <div
                className={cn('rounded-lg p-2 bg-gray-100/50 h-full overflow-y-auto overscroll-contain scrollbar-thin')}
            >
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={orderedItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                        <div className="flex flex-col gap-1">
                            {orderedItems.map((item, index) => {
                                if (item.type === 'column') {
                                    return (
                                        <SortableColumn
                                            key={item.id}
                                            column={item.column}
                                            onToggle={handleToggle}
                                            onMoveUp={handleMoveUp}
                                            onMoveDown={handleMoveDown}
                                            isFirst={index === 0}
                                            isLast={index === orderedItems.length - 1}
                                        />
                                    );
                                } else {
                                    const group = groups.find((g) => g.id === item.groupId);
                                    if (!group) return null;
                                    const groupColumns = columns.filter((c) => c.group === item.groupId);
                                    return (
                                        <SortableColumnGroup
                                            key={item.id}
                                            group={group}
                                            groupColumns={groupColumns}
                                            columns={columns}
                                            onToggle={handleToggle}
                                            onToggleGroup={handleToggleGroup}
                                            onColumnsChange={onColumnsChange}
                                            expandedGroups={expandedGroups}
                                            onToggleExpand={handleToggleExpand}
                                            onMoveUp={handleMoveUp}
                                            onMoveDown={handleMoveDown}
                                            isFirst={index === 0}
                                            isLast={index === orderedItems.length - 1}
                                        />
                                    );
                                }
                            })}
                        </div>
                    </SortableContext>
                </DndContext>
            </div>
        </div>
    );
}

ColumnsSelector.propTypes = {
    className: PropTypes.string,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            active: PropTypes.bool.isRequired,
            group: PropTypes.string,
        }),
    ).isRequired,
    onColumnsChange: PropTypes.func.isRequired,
    groups: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        }),
    ),
};
